import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Image, Lightbulb, HelpCircle } from 'lucide-react';
import Header from '../components/layout/Header';
import QrUpload from '../components/analysis/QrUpload';
import QrGuideOverlay from '../components/analysis/QrGuideOverlay';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { api } from '../services/api';

export default function AnalyzeQrPage() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setError('');
    setLoading(true);
    try {
      const data = await api.analyzeQrCode(file);
      api.saveToHistory(data);
      navigate('/results', { state: { type: 'qr', data } });
    } catch {
      setLoading(false);
      setError('Impossible de lire ce QR code. Essayez avec une photo plus nette.');
    }
  };

  if (loading) return <LoadingSpinner url={selectedFile?.name || 'QR code'} currentStep={1} />;

  return (
    <div className="page-container">
      <Header showBack backLabel="Scanner un QR code" onBack={() => navigate('/analyze')} />
      <main className="md:grid md:grid-cols-[360px_1fr] md:gap-6 md:items-start md:pt-6">
        <section>
          <div className="mx-4 mt-6 md:mt-0 card border-l-4 border-l-amber-400 bg-amber-50/50">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-7 h-7 text-amber-500 flex-shrink-0 mt-1" />
              <div>
                <p className="text-text-primary text-lg leading-relaxed">Pointez votre caméra vers le QR code pour une analyse instantanée de la sécurité du marchand ou de la transaction.</p>
                <button
                  type="button"
                  onClick={() => setShowGuide(true)}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-accent-blue active:opacity-70 transition-opacity"
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                  Comment faire ?
                </button>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
              <input ref={galleryInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
              <button type="button" onClick={() => cameraInputRef.current?.click()} className="flex-1 btn-primary text-sm h-14" aria-label="Utiliser la caméra pour scanner un QR code"><Camera className="w-5 h-5" />Utiliser la caméra</button>
              <button type="button" onClick={() => galleryInputRef.current?.click()} className="flex-1 btn-secondary text-sm h-14" aria-label="Importer une photo de QR code"><Image className="w-5 h-5" />Importer une photo</button>
            </div>
          </div>
          {selectedFile ? <p className="mx-4 mt-3 text-center text-sm text-accent-blue font-medium">Photo sélectionnée : {selectedFile.name}</p> : null}
          {error ? <div className="mx-4 mt-4"><ErrorMessage message={error} /></div> : null}
        </section>

        <section>
          <QrUpload onFileSelect={() => galleryInputRef.current?.click()} />
          <p className="mx-8 mt-8 text-center text-text-muted text-lg leading-relaxed">La photo ne sera pas sauvegardée dans votre galerie.</p>
        </section>
      </main>

      {showGuide && <QrGuideOverlay onClose={() => setShowGuide(false)} />}
    </div>
  );
}
